const READY_EVENT = 'rivo-js-loaded';
const POLL_INTERVAL_MS = 100;
const DEFAULT_TIMEOUT_MS = 10000;

let readyPromise = null;
let customerDetailsPromise = null;
let shopRewardsPromise = null;

function isRivoReady() {
  return Boolean(window.RivoJS && typeof window.RivoJS.getCustomerDetails === 'function');
}

// NOTE on retry semantics: when a memoized promise (readyPromise / customerDetailsPromise /
// shopRewardsPromise) rejects, the cache is cleared so the NEXT caller will create a fresh
// request. Callers that were awaiting the failed promise still receive the rejection — by
// design. If a caller needs retry, it should catch and call again.
export function rivoReady({ timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  if (readyPromise) return readyPromise;

  readyPromise = new Promise((resolve, reject) => {
    if (isRivoReady()) return resolve(window.RivoJS);

    let pollInterval = null;
    let timeoutId = null;

    const cleanup = () => {
      document.removeEventListener(READY_EVENT, onReady);
      if (pollInterval) clearInterval(pollInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const onReady = () => {
      if (!isRivoReady()) return;
      cleanup();
      resolve(window.RivoJS);
    };

    // Cover two race cases: (a) event fires after listener attaches — listener wins;
    // (b) RivoJS was already wired before our listener attached — polling catches it.
    document.addEventListener(READY_EVENT, onReady);
    pollInterval = setInterval(() => {
      if (isRivoReady()) {
        cleanup();
        resolve(window.RivoJS);
      }
    }, POLL_INTERVAL_MS);

    timeoutId = setTimeout(() => {
      cleanup();
      readyPromise = null;
      reject(new Error('RivoJS not available'));
    }, timeoutMs);
  });

  return readyPromise;
}

export function resetCustomerDetails() {
  customerDetailsPromise = null;
}

export function resetShopRewards() {
  shopRewardsPromise = null;
}

export function getCustomerDetails({ timeoutMs } = {}) {
  if (customerDetailsPromise) return customerDetailsPromise;

  customerDetailsPromise = rivoReady({ timeoutMs })
    .then((rivo) => rivo.getCustomerDetails())
    .then((response) => response?.customer ?? response ?? null)
    .catch((error) => {
      customerDetailsPromise = null;
      throw error;
    });

  return customerDetailsPromise;
}

export function getShopRewards({ timeoutMs } = {}) {
  if (shopRewardsPromise) return shopRewardsPromise;

  shopRewardsPromise = rivoReady({ timeoutMs })
    .then((rivo) => {
      if (typeof rivo.getShopRewards !== 'function') {
        throw new Error('RivoJS.getShopRewards not available');
      }
      return rivo.getShopRewards();
    })
    .then((response) => {
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.rewards)) return response.rewards;
      return [];
    })
    .catch((error) => {
      shopRewardsPromise = null;
      throw error;
    });

  return shopRewardsPromise;
}

export async function redeemReward(rewardId) {
  const rivo = await rivoReady();
  if (typeof rivo.redeemReward === 'function') {
    return rivo.redeemReward(rewardId);
  }
  if (typeof rivo.createRewardRedemption === 'function') {
    return rivo.createRewardRedemption(rewardId);
  }
  throw new Error('RivoJS redemption method not available');
}
